import { useSyncExternalStore } from 'react';
import type { PublicState } from '../../shared/protocol';

// ============================================================
// v14 BOLT-049 — device-local game history (no accounts).
// Entries are written ONLY from post-round snapshots (`intermission`/`ended`
// — the phases where the server has already revealed the round); the event
// timeline is cached AT WRITE TIME (kind + offset + label, never
// coordinates) so it outlives server replay pruning. localStorage, capped.
// ============================================================

export interface HistoryEvent {
  kind: string;
  t: number;
  label?: string;
}

export interface HistoryEntry {
  id: string; // `${code}-r${round}`
  code: string;
  round: number;
  endedAt: number;
  role: 'hider' | 'seeker' | null; // YOUR side that round
  outcome: 'caught' | 'survived';
  finalMin: number;
  gifUrl: string | null;
  jsonUrl: string | null;
  events?: HistoryEvent[]; // cached timeline
}

const KEY = 'bolt:history';
const CAP = 50;

export function readHistory(): HistoryEntry[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '[]') as HistoryEntry[];
  } catch {
    return [];
  }
}

function write(list: HistoryEntry[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, CAP)));
  } catch {
    /* private mode / quota — history is best-effort */
  }
  emit();
}

/** Append-or-merge (replay urls + events land after the entry). Newest first. */
export function upsertHistory(entry: HistoryEntry): void {
  const list = readHistory();
  const i = list.findIndex((e) => e.id === entry.id);
  if (i >= 0) {
    list[i] = { ...list[i], ...entry, events: entry.events ?? list[i].events };
  } else {
    list.unshift(entry);
  }
  write(list);
}

export function attachEvents(id: string, events: HistoryEvent[]): void {
  const list = readHistory();
  const i = list.findIndex((e) => e.id === id);
  if (i < 0) return;
  list[i] = { ...list[i], events };
  write(list);
}

// The timeline keeps a HUMAN summary of each event — kind, round-offset, a
// short label — and DROPS the raw data (card events carry seeker coords, the
// anchor carries the hider's). Post-round or not, the cache stays clean.
const SHOW_KINDS = new Set([
  'headstart',
  'card',
  'pending',
  'veto',
  'curse',
  'bonus',
  'portal_armed',
  'portal_triggered',
  'portal_cleared',
  'photo_open',
  'photo',
  'photo_fail',
  'thermo_open',
  'caught',
  'timeup',
]);

export function sanitizeEvents(raw: unknown): HistoryEvent[] {
  const events = Array.isArray((raw as { events?: unknown[] })?.events)
    ? ((raw as { events: { kind?: unknown; t?: unknown; data?: Record<string, unknown> }[] }).events ?? [])
    : [];
  return events
    .filter((e) => typeof e.kind === 'string' && SHOW_KINDS.has(e.kind))
    .map((e) => {
      const d = (e.data ?? {}) as Record<string, unknown>;
      const label =
        typeof d.card === 'string'
          ? d.card
          : typeof d.label === 'string'
            ? d.label
            : typeof d.kind === 'string'
              ? d.kind
              : undefined;
      return { kind: String(e.kind), t: Number(e.t) || 0, ...(label ? { label } : {}) };
    });
}

/** Harvest every finished round the snapshot proves (post-round phases only);
 *  fetch-and-cache the event timeline once per entry when the replay lands. */
export function harvestHistory(state: PublicState | null): void {
  if (!state) return;
  if (state.phase !== 'intermission' && state.phase !== 'ended') return;
  for (const r of state.series.history ?? []) {
    const id = `${state.code}-r${r.roundIndex}`;
    const existing = readHistory().find((e) => e.id === id);
    upsertHistory({
      id,
      code: state.code,
      round: r.roundIndex,
      endedAt: existing?.endedAt ?? state.timer.serverNow,
      role: state.you.team ? (state.you.team === r.hiderTeam ? 'hider' : 'seeker') : null,
      outcome: r.caught ? 'caught' : 'survived',
      finalMin: r.finalMin,
      gifUrl: r.replay?.gifUrl ?? null,
      jsonUrl: r.replay?.jsonUrl ?? null,
    });
    if (r.replay?.jsonUrl && !existing?.events) {
      void fetch(r.replay.jsonUrl)
        .then((res) => (res.ok ? res.json() : null))
        .then((json) => {
          if (json) attachEvents(id, sanitizeEvents(json));
        })
        .catch(() => {
          /* replay pruned or offline — the entry stays, timeline shows expired */
        });
    }
  }
}

// ---- the overlay store (the helpStore pattern) ----
let open = false;
const subs = new Set<() => void>();
const emit = () => subs.forEach((s) => s());

export const historyStore = {
  snapshot: () => open,
  open() {
    if (!open) {
      open = true;
      emit();
    }
  },
  close() {
    if (open) {
      open = false;
      emit();
    }
  },
  subscribe(cb: () => void) {
    subs.add(cb);
    return () => subs.delete(cb);
  },
};

export function useHistoryOpen(): boolean {
  return useSyncExternalStore(historyStore.subscribe, historyStore.snapshot, historyStore.snapshot);
}

export function useHistoryEntries(): HistoryEntry[] {
  const json = useSyncExternalStore(
    historyStore.subscribe,
    () => {
      try {
        return localStorage.getItem(KEY) ?? '[]';
      } catch {
        return '[]';
      }
    },
    () => '[]',
  );
  try {
    return JSON.parse(json) as HistoryEntry[];
  } catch {
    return [];
  }
}
